import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSupabase } from '../../lib/lazySupabase';
import { installAuthListener, useAuthStore } from '../../store/authStore';
import { initOneSignalWeb, oneSignalLogin, oneSignalLogout } from '@golfauth/utils';
import { proNeedsPasswordSetup } from '../../lib/proPasswordGate';

/**
 * Mount-once auth bootstrap. Two jobs:
 *
 *   1. Install the `onAuthStateChange` listener that lives in
 *      authStore. Defers the listener install (and therefore the
 *      Supabase SDK import) to a `useEffect` after first paint so
 *      the marketing homepage doesn't block on the 32 KiB SDK
 *      chunk (Lighthouse Win 3 / D8).
 *
 *   2. On every nav change, peek the current session and forward
 *      `?code=` PKCE callbacks / `#access_token=` hash flows to
 *      /auth/callback, and redirect users with
 *      `registration_complete: false` metadata to /set-password.
 */

// Sync, no-SDK check for whether the visitor will have a session ready
// for `onAuthStateChange` to consume on first fire. Only signals that
// are restored synchronously (or that installAuthListener awaits
// before registering the listener) count here. Bugbot 2026-06-26:
// don't lump in PKCE `?code=` or hash `#access_token=` flows — those
// need an explicit exchangeCodeForSession / setSession network call
// that the `/auth/callback` route owns. Treating them as ready would
// cause `INITIAL_SESSION` to fire with session=null, mark the store
// initialized-as-unauthenticated, and flash protected routes through
// to login until the late SIGNED_IN landed.
//
// What this DOES count as ready:
//   1. localStorage `sb-<project>-auth-token` — returning visitor
//      with a persisted session.
//   2. URL query params `token` AND `refresh_token` — Core/Capacitor
//      cross-WebView handoff. supabase.ts strips them on boot and
//      installAuthListener() awaits `supabaseAuthReady` BEFORE
//      registering the listener, so INITIAL_SESSION reflects the
//      restored session.
//
// PKCE + hash flows fall through to the idle-deferred path. By the
// time `/auth/callback` finishes its exchange, the listener attaches
// via the late path and picks up the resulting SIGNED_IN cleanly.
function hasInboundAuthSignal(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      // supabase-js writes keys like `sb-<project-ref>-auth-token`.
      // Matching the prefix avoids hard-coding the project ref here.
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        return true;
      }
    }
  } catch {
    /* localStorage can throw under strict cookie policies; treat as
       "no session" and rely on the cookie / URL checks below. */
  }
  // Production shared-session path stores JWTs in cookies named
  // `ga_sb-<ref>-auth-token` (see packages/auth createCookieStorage).
  // Without this, returning PWA users look anonymous → idle-deferred
  // auth install → ProtectedRoute spinner until idle fires (or forever
  // if initializeAuth hangs mid-flight).
  try {
    if (/(?:^|;\s*)ga_sb-[^=]*auth[_-]token=/.test(document.cookie)) {
      return true;
    }
  } catch {
    /* document.cookie can throw in sandboxed iframes */
  }
  try {
    const search = new URLSearchParams(window.location.search);
    // Both params required — a stray `?token=` alone is not a Core
    // handoff and shouldn't pre-empt the idle defer.
    if (search.has('token') && search.has('refresh_token')) return true;
  } catch {
    /* URL parsing should not throw, but guard for completeness. */
  }
  return false;
}

// requestIdleCallback shim. Lighthouse measures within ~5 s of load on
// throttled CPU, so 1500 ms fallback keeps us under the budget while
// still pushing the SDK download past LCP.
function whenIdle(cb: () => void): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(cb, { timeout: 1500 });
  } else {
    window.setTimeout(cb, 1500);
  }
}

export default function AuthStateHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  // Listener install — runs once. Idempotent on the authStore side
  // (a module-scope `installed` flag protects against double-register
  // in StrictMode dev double-mount).
  //
  // For visitors with an inbound auth signal (persisted session OR
  // URL handoff tokens OR PKCE/hash callback) we install immediately
  // so the navbar / protected routes get the session without a flash.
  // For genuinely anonymous marketing visitors we defer to idle — the
  // SDK chunk + GoTrueClient parse (~50 KiB on the wire per
  // Lighthouse) come off the LCP path.
  useEffect(() => {
    if (hasInboundAuthSignal()) {
      void installAuthListener();
    } else {
      whenIdle(() => { void installAuthListener(); });
    }
  }, []);

  // PWA reopen / bfcache: if auth bootstrap froze mid-flight
  // (!isInitialized), kick initializeAuth again when the tab returns.
  useEffect(() => {
    const kick = () => {
      const { isInitialized, isLoading } = useAuthStore.getState();
      if (!isInitialized || isLoading) {
        void useAuthStore.getState().initializeAuth();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') kick();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) kick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', kick);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', kick);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // OneSignal Web SDK — only load when there's a real authenticated
  // user. On the marketing homepage (anonymous visitor) push
  // notifications have nothing to bind to, so eagerly injecting the
  // script was pure waste (~35 KiB per Lighthouse 2026-06-26). CSP
  // allowlists cdn.onesignal.com for post-sign-in injection. Listen
  // to the auth-state-change stream and init the SDK lazily once
  // SIGNED_IN fires.
  useEffect(() => {
    if (!import.meta.env.VITE_ONESIGNAL_APP_ID) return;

    let unsub: (() => void) | null = null;
    let cancelled = false;
    // Two-flag model — bugbot caught that a single one-way init flag
    // gated BOTH the SDK injection AND the per-user login, so:
    //   (a) after SIGNED_OUT → SIGNED_IN within the same tab, the
    //       second SIGNED_IN saw initialized=true and skipped
    //       oneSignalLogin — OneSignal kept the previous user's
    //       external_id until a hard reload.
    //   (b) anonymous-at-load visitors who signed in during the
    //       async getSupabase() window could miss the SIGNED_IN
    //       event entirely (replayed as INITIAL_SESSION on listener
    //       registration, which the handler ignored).
    // Split tracking: sdkInjected gates the one-time
    // initOneSignalWeb() call; oneSignalLogin always fires for every
    // session-bearing event. INITIAL_SESSION is added to the trigger
    // list so the late-listener race is covered.
    let sdkInjected = false;
    const ensureSdkInjected = () => {
      if (sdkInjected) return;
      sdkInjected = true;
      initOneSignalWeb({
        appId: import.meta.env.VITE_ONESIGNAL_APP_ID as string | undefined,
      });
    };
    const bindUser = (userId: string) => {
      ensureSdkInjected();
      oneSignalLogin(userId);
    };

    // Bootstrap + listener-register helper. Pulls the Supabase chunk
    // and wires onAuthStateChange. Called immediately when an inbound
    // auth signal is present; deferred to idle otherwise so anonymous
    // marketing visits don't pay the ~50 KiB SDK download just to
    // register a listener that would never fire (bugbot 2026-06-26 —
    // gating OneSignal independently of the install path was needed
    // because VITE_ONESIGNAL_APP_ID being set was effectively
    // un-deferring the Supabase chunk).
    const wireSupabase = async () => {
      if (cancelled) return;
      // Bootstrap path — only runs when a session is known to exist
      // so we don't trigger a no-op getSession() roundtrip on fresh
      // anonymous tabs that later signed in (the listener below
      // handles that case via INITIAL_SESSION / SIGNED_IN).
      if (hasInboundAuthSignal()) {
        const supabase = await getSupabase();
        if (cancelled) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session?.user?.id) bindUser(session.user.id);
      }

      // Listener path: covers fresh sign-in, token refresh, sign-out,
      // AND late attachment (INITIAL_SESSION replays current state
      // when the listener registers — the only signal we get if a
      // user signed in during the getSupabase() await above).
      const supabase = await getSupabase();
      if (cancelled) return;
      const { data } = supabase.auth.onAuthStateChange((event, sess) => {
        if (
          event === 'INITIAL_SESSION' ||
          event === 'SIGNED_IN' ||
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          if (sess?.user?.id) bindUser(sess.user.id);
        } else if (event === 'SIGNED_OUT') {
          if (sdkInjected) void oneSignalLogout();
        }
      });
      if (cancelled) {
        data.subscription.unsubscribe();
        return;
      }
      unsub = () => data.subscription.unsubscribe();
    };

    if (hasInboundAuthSignal()) {
      void wireSupabase();
    } else {
      // No session expected at boot — defer the supabase chunk +
      // listener install past LCP. If the user signs in during the
      // idle window the listener will pick them up via SIGNED_IN /
      // INITIAL_SESSION once it eventually attaches.
      whenIdle(() => { void wireSupabase(); });
    }

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  useEffect(() => {
    // Handle initial session on page load
    const checkInitialSession = async () => {
      // Skip if password setup is in progress to prevent redirect loops
      const passwordSetupInProgress = sessionStorage.getItem('password_setup_in_progress');
      if (passwordSetupInProgress === 'true') {
        return;
      }

      // Soft backup for ProtectedRoute — Pros with must_set_password /
      // incomplete registration bounce to set-password.
      const shouldRedirectToSetPassword = proNeedsPasswordSetup;

      // If we have a code in the URL, forward to AuthCallback to handle PKCE exchange
      // — EXCEPT on password reset / set-password routes, which own the exchange
      // themselves. Stealing recovery `?code=` into /auth/callback is what dumped
      // Pros on "Email Verified → Log In" with no password form.
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const path = location.pathname;
      const isPasswordRoute =
        path === '/pro/reset-password' ||
        path === '/reset-password' ||
        path === '/pro/set-password' ||
        path === '/set-password';

      if (code) {
        if (path !== '/auth/callback' && !isPasswordRoute) {
          navigate(`/auth/callback${window.location.search}`, { replace: true });
        }
        return;
      }

      const hashParams = new URLSearchParams((window.location.hash || '').substring(1));
      const accessToken = hashParams.get('access_token');
      if (
        accessToken &&
        path !== '/auth/callback' &&
        !isPasswordRoute
      ) {
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }

      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        if (shouldRedirectToSetPassword(session.user)) {
          if (
            location.pathname !== '/pro/set-password' &&
            location.pathname !== '/pro/reset-password'
          ) {
            navigate('/pro/set-password', { replace: true });
          }
        }
      }
    };

    checkInitialSession();
  }, [navigate, location.pathname]);

  // Do NOT auto-redirect golfers from home to /golfer. Let them see the Fitting
  // home (PRO, Find a Professional, Golfer/Professional Sign In) so 5173 shows choice options.
  // They can navigate to Core via Golfer Sign In or /golfer when they choose.

  return null;
}
